/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import PropTypes from 'prop-types'
import React, {useCallback, useMemo} from 'react'
import _ from 'lodash'
import ButtonType from './button-type'
import LinkType from './link-type'
import {useCasing} from '../../hooks/use-title-case'
import {useBooxi} from '../../hooks/use-booxi'
import {AuthHelpers, useAuthHelper} from '@salesforce/commerce-sdk-react'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'

export const ActionType = {
    Mailto: 'Mailto',
    Link: 'Link',
    Account: 'Account',
    BookService: 'Book service'
}

const Link = ({
    url: _url,
    externalUrl: _externalUrl,
    serviceId = null,
    type = 'primary',
    useNavLink = false,
    isExternal = false,
    openLinkInNewTab = false,
    text,
    iconName,
    iconStyles = {},
    iconComponent,
    buttonStyles,
    onLinkClick,
    children,
    inactive = false,
    buttonType = 'button',
    isTitleCase = true,
    textUnderLine = false,
    ...props
}) => {
    const navigate = useNavigation()
    const logout = useAuthHelper(AuthHelpers.Logout)
    const {instantiateBooxi} = useBooxi()
    const {updatedText} = useCasing({isTitleCase, text})
    const url = props?.actionType === ActionType.Mailto ? null : _url
    const externalUrl =
        props?.actionType === ActionType.Mailto ? false : _externalUrl
    const theme = useMemo(() => {
        const themeProps = props?.theme?.fields
        if (!themeProps) return {}

        return {
            ...themeProps,
            background: themeProps?.backgroundColor || 'unset',
            _hover: {
                fontWeight: 'bold',
                backgroundColor: themeProps?.backgroundColor || 'unset'
            }
        }
    }, [])
    const newProps = useMemo(() => {
        const _newProps = {
            ...props,
            active: props?.active ? `${props?.active}` : 'true',
            ...theme
        }

        delete _newProps?.actionType
        delete _newProps?.textWrapMode
        delete _newProps?.itemsCollection
        delete _newProps?.listType
        delete _newProps?.headingType
        delete _newProps?.isNested
        delete _newProps?.onLinkClick

        return _newProps
    }, [props])

    const LinkActionsByType = {
        Mailto: () => (window.location.href = _url),
        'Book service': () => instantiateBooxi(serviceId),
        Logout: async () => {
            await logout()
            navigate('/login')
        }
    }

    const _onLinkClick = useCallback(
        (e) => {
            const actionType = props?.actionType
            if (
                !actionType ||
                [ActionType.Link, ActionType.Account].includes(actionType)
            ) {
                if (onLinkClick) onLinkClick()
                return
            }

            const linkAction = LinkActionsByType?.[actionType]
            if (linkAction) linkAction()
            if (e) e.preventDefault()
        },
        [props]
    )

    return (
        <>
            {url || externalUrl ? (
                <LinkType
                    onLinkClick={_onLinkClick}
                    href={url}
                    externalUrl={externalUrl}
                    type={type}
                    useNavLink={useNavLink}
                    isExternal={isExternal}
                    openLinkInNewTab={openLinkInNewTab}
                    text={updatedText}
                    icon={iconName}
                    iconStyles={iconStyles}
                    iconComponent={iconComponent}
                    buttonStyles={buttonStyles}
                    textUnderLine={textUnderLine}
                    {..._.omit(newProps, ['to'])}
                >
                    {children}
                </LinkType>
            ) : (
                <ButtonType
                    onLinkClick={_onLinkClick}
                    type={type}
                    text={updatedText}
                    icon={iconName}
                    iconComponent={iconComponent}
                    buttonStyles={buttonStyles}
                    inactive={inactive}
                    iconStyles={iconStyles}
                    buttonType={buttonType}
                    textUnderLine={textUnderLine}
                    {...newProps}
                >
                    {children}
                </ButtonType>
            )}
        </>
    )
}

Link.displayName = 'Link'

Link.propTypes = {
    url: PropTypes.string,
    externalUrl: PropTypes.string,
    useNavLink: PropTypes.bool,
    /**
     * isExternal means it is external link/complete link which starts with https://
     */
    isExternal: PropTypes.bool,
    openLinkInNewTab: PropTypes.bool,
    text: PropTypes.any,
    buttonStyles: PropTypes.object,
    onLinkClick: PropTypes.func,
    iconName: PropTypes.string,
    iconStyles: PropTypes.object,
    iconComponent: PropTypes.object,
    children: PropTypes.node,
    type: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'unstyled']),
    inactive: PropTypes.bool,
    buttonType: PropTypes.oneOf(['button', 'submit', 'reset']),
    isTitleCase: PropTypes.bool,
    textUnderLine: PropTypes.bool,
    serviceId: PropTypes.string,
    actionType: PropTypes.oneOf([
        ActionType.Mailto,
        ActionType.Link,
        ActionType.BookService,
        ActionType.Account
    ]),
    active: PropTypes.string,
    theme: PropTypes.shape({
        fields: PropTypes.shape({
            color: PropTypes.string,
            backgroundColor: PropTypes.string
        })
    })
}

export default React.memo(Link)
